import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Choisir une planification SANS ecrire d'expression cron.
 *
 * POURQUOI CE COMPOSANT EXISTE. Le champ demandait l'expression brute, et un utilisateur de
 * console tape « 2***2* » -- cinq champs separes par des espaces ne sont pas une interface.
 * Kubernetes refuse alors le CronJob (`spec.schedule: Invalid value: expected exactly 5
 * fields`), mais trop tard : l'API a repondu 201, le values.yaml est ecrit, et c'est ArgoCD
 * qui echoue, c'est-a-dire nulle part ou le client regarde. Mesure sur l'apiserver le
 * 2026-11-01.
 *
 * ON NE REMPLACE PAS LA SAISIE LIBRE, ON LA RELEGUE. Le constructeur ne peut produire que des
 * expressions valides, mais il ne couvre pas tout ce que cron sait dire (« toutes les 15 min,
 * en semaine seulement »). Retirer la saisie libre serait RETIRER une capacite -- elle reste
 * donc disponible en dernier choix, et validee.
 *
 * L'HEURE EST EN UTC, ET C'EST DIT. Le chart accepte `cron.timeZone` mais l'API ne le
 * renseigne pas encore : le noeud est en `Etc/UTC` (mesure), donc « 03:00 » declenche a 04h00
 * a Douala. Un sélecteur d'horloge qui tairait ce decalage serait un rapport faux -- c'est
 * precisement le travers que ce composant corrige.
 */

type Frequence = 'minutes' | 'heure' | 'jour' | 'semaine' | 'mois' | 'libre';

interface Etat {
  frequence: Frequence;
  pas: number;       // toutes les N minutes
  minute: number;    // minute de l'heure
  heureMin: string;  // "HH:MM" du <input type="time">
  jourSemaine: number;
  jourMois: number;
  libre: string;
}

const DEFAUT: Etat = {
  frequence: 'jour', pas: 15, minute: 0, heureMin: '02:00',
  jourSemaine: 1, jourMois: 1, libre: '',
};

/** Un champ cron simple : rend le nombre, ou null si ce n'est pas un entier nu. */
const entier = (s: string): number | null => (/^\d+$/.test(s) ? parseInt(s, 10) : null);

/**
 * Reconnait les formes que CE constructeur produit, et rien de plus.
 *
 * C'est deliberement etroit : une expression qu'il ne sait pas reconstituer tombe en saisie
 * libre AVEC sa valeur, plutot que d'etre approximee. Sans ca, l'ecran « Modifier » afficherait
 * une planification qui n'est pas celle qui tourne -- le defaut deja paye sur `extraEnv`.
 */
export const analyser = (expression: string | null | undefined): Etat => {
  const brut = (expression || '').trim();
  if (!brut) return { ...DEFAUT };
  const champs = brut.split(/\s+/);
  if (champs.length !== 5) return { ...DEFAUT, frequence: 'libre', libre: brut };

  const [m, h, dom, mon, dow] = champs;
  const deuxChiffres = (n: number) => String(n).padStart(2, '0');

  if (mon === '*') {
    const pas = /^\*\/(\d+)$/.exec(m);
    if (pas && h === '*' && dom === '*' && dow === '*') {
      return { ...DEFAUT, frequence: 'minutes', pas: parseInt(pas[1], 10) };
    }
    const min = entier(m);
    if (min !== null && h === '*' && dom === '*' && dow === '*') {
      return { ...DEFAUT, frequence: 'heure', minute: min };
    }
    const hr = entier(h);
    if (min !== null && hr !== null) {
      const heureMin = `${deuxChiffres(hr)}:${deuxChiffres(min)}`;
      if (dom === '*' && dow === '*') return { ...DEFAUT, frequence: 'jour', heureMin };
      const jsem = entier(dow);
      if (dom === '*' && jsem !== null) {
        return { ...DEFAUT, frequence: 'semaine', heureMin, jourSemaine: jsem };
      }
      const jmois = entier(dom);
      if (jmois !== null && dow === '*') {
        return { ...DEFAUT, frequence: 'mois', heureMin, jourMois: jmois };
      }
    }
  }
  return { ...DEFAUT, frequence: 'libre', libre: brut };
};

/** Compose l'expression des cinq champs. C'est la seule chose qui part a l'API. */
export const composer = (e: Etat): string => {
  const [hh, mm] = (e.heureMin || '00:00').split(':');
  const h = parseInt(hh, 10) || 0;
  const m = parseInt(mm, 10) || 0;
  switch (e.frequence) {
    case 'minutes': return `*/${Math.min(Math.max(e.pas, 1), 59)} * * * *`;
    case 'heure':   return `${Math.min(Math.max(e.minute, 0), 59)} * * * *`;
    case 'jour':    return `${m} ${h} * * *`;
    case 'semaine': return `${m} ${h} * * ${e.jourSemaine}`;
    case 'mois':    return `${m} ${h} ${Math.min(Math.max(e.jourMois, 1), 31)} * *`;
    case 'libre':   return e.libre.trim();
  }
};

interface Props {
  value: string;
  onChange: (expression: string) => void;
}

const CronScheduleBuilder: React.FC<Props> = ({ value, onChange }) => {
  const { t } = (useTranslation as any)('deployments');
  /* L'ETAT EST DERIVE DE `value`, il n'est pas duplique. Garder une copie locale ferait
     diverger l'ecran de ce qui sera envoye des que le parent change la valeur (pre-remplissage
     depuis git, reinitialisation du formulaire). */
  const etat = useMemo(() => analyser(value), [value]);
  const poser = (modif: Partial<Etat>) => onChange(composer({ ...etat, ...modif }));

  const jours = [
    t('cronBuilder.dim'), t('cronBuilder.lun'), t('cronBuilder.mar'), t('cronBuilder.mer'),
    t('cronBuilder.jeu'), t('cronBuilder.ven'), t('cronBuilder.sam'),
  ];

  const resume = useMemo(() => {
    switch (etat.frequence) {
      case 'minutes': return t('cronBuilder.resumeMinutes', { n: etat.pas });
      case 'heure':   return t('cronBuilder.resumeHeure', { m: etat.minute });
      case 'jour':    return t('cronBuilder.resumeJour', { h: etat.heureMin });
      case 'semaine': return t('cronBuilder.resumeSemaine', { j: jours[etat.jourSemaine % 7], h: etat.heureMin });
      case 'mois':    return t('cronBuilder.resumeMois', { d: etat.jourMois, h: etat.heureMin });
      default:        return null;
    }
  }, [etat, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const champ = 'px-3 py-2 text-sm border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500';

  return (
    <div className="space-y-3">
      <select
        value={etat.frequence}
        onChange={(e) => poser({ frequence: e.target.value as Frequence })}
        className={`w-full ${champ}`}
      >
        <option value="minutes">{t('cronBuilder.freqMinutes')}</option>
        <option value="heure">{t('cronBuilder.freqHeure')}</option>
        <option value="jour">{t('cronBuilder.freqJour')}</option>
        <option value="semaine">{t('cronBuilder.freqSemaine')}</option>
        <option value="mois">{t('cronBuilder.freqMois')}</option>
        <option value="libre">{t('cronBuilder.freqLibre')}</option>
      </select>

      <div className="flex flex-wrap items-end gap-3">
        {etat.frequence === 'minutes' && (
          <label className="text-xs text-text-secondary">
            <span className="block mb-1">{t('cronBuilder.toutesLesMinutes')}</span>
            <input type="number" min={1} max={59} value={etat.pas}
              onChange={(e) => poser({ pas: parseInt(e.target.value, 10) || 1 })}
              className={`w-24 ${champ}`} />
          </label>
        )}

        {etat.frequence === 'heure' && (
          <label className="text-xs text-text-secondary">
            <span className="block mb-1">{t('cronBuilder.aLaMinute')}</span>
            <input type="number" min={0} max={59} value={etat.minute}
              onChange={(e) => poser({ minute: parseInt(e.target.value, 10) || 0 })}
              className={`w-24 ${champ}`} />
          </label>
        )}

        {etat.frequence === 'semaine' && (
          <label className="text-xs text-text-secondary">
            <span className="block mb-1">{t('cronBuilder.leJour')}</span>
            <select value={etat.jourSemaine}
              onChange={(e) => poser({ jourSemaine: parseInt(e.target.value, 10) })}
              className={champ}>
              {jours.map((j, i) => <option key={i} value={i}>{j}</option>)}
            </select>
          </label>
        )}

        {etat.frequence === 'mois' && (
          <label className="text-xs text-text-secondary">
            <span className="block mb-1">{t('cronBuilder.leJourDuMois')}</span>
            <input type="number" min={1} max={31} value={etat.jourMois}
              onChange={(e) => poser({ jourMois: parseInt(e.target.value, 10) || 1 })}
              className={`w-24 ${champ}`} />
          </label>
        )}

        {/* `type="time"` : le selecteur numerique natif du navigateur, exactement ce qu'on
            veut a la place de deux champs cron. Il rend toujours "HH:MM". */}
        {(etat.frequence === 'jour' || etat.frequence === 'semaine' || etat.frequence === 'mois') && (
          <label className="text-xs text-text-secondary">
            <span className="block mb-1">{t('cronBuilder.aLHeure')}</span>
            <input type="time" value={etat.heureMin}
              onChange={(e) => poser({ heureMin: e.target.value || '00:00' })}
              className={champ} />
          </label>
        )}

        {etat.frequence === 'libre' && (
          <label className="text-xs text-text-secondary flex-1 min-w-[220px]">
            <span className="block mb-1">{t('cronBuilder.expressionLibre')}</span>
            <input type="text" value={etat.libre} placeholder="*/15 * * * 1-5"
              onChange={(e) => poser({ libre: e.target.value })}
              className={`w-full font-mono ${champ}`} />
          </label>
        )}
      </div>

      {/* Le jour du mois au-dela de 28 saute les mois courts : fevrier n'a pas de 30. Le dire
          ici evite un « mon cron ne tourne pas » qui n'a rien d'un defaut. */}
      {etat.frequence === 'mois' && etat.jourMois > 28 && (
        <p className="text-xs text-yellow-300/90">{t('cronBuilder.avertJourMois')}</p>
      )}

      {resume && <p className="text-xs text-text-secondary">{resume}</p>}

      <p className="text-xs text-text-tertiary">
        {t('cronBuilder.fuseauUtc')}
        {' '}
        <span className="text-text-quaternary">{t('cronBuilder.expressionEnvoyee')}</span>{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-3 border border-border-1 font-mono text-text-secondary">
          {composer(etat) || '—'}
        </code>
      </p>
    </div>
  );
};

export default CronScheduleBuilder;
